import { useEffect, useRef, useState, type ReactNode } from 'react';

type InputDialogVariant = 'primary' | 'danger';

interface InputDialogProps {
  open: boolean;
  title: string;
  description?: string;
  initialValue?: string;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  confirmText: string;
  cancelText: string;
  variant?: InputDialogVariant;
  onConfirm: (value: string) => void;
  onCancel: () => void;
  children?: ReactNode;
}

export function InputDialog({
  open,
  title,
  description,
  initialValue = '',
  placeholder,
  multiline = false,
  rows = 4,
  confirmText,
  cancelText,
  variant = 'primary',
  onConfirm,
  onCancel,
  children
}: InputDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    if (!open) return;
    setValue(initialValue);
  }, [initialValue, open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => {
      const target = multiline ? textareaRef.current : inputRef.current;
      target?.focus();
      target?.select();
    }, 0);
    return () => window.clearTimeout(t);
  }, [multiline, open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        onConfirm(value);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onCancel, onConfirm, open, value]);

  if (!open) return null;

  const confirmClass =
    variant === 'danger'
      ? 'btn bg-red-600 hover:bg-red-700 text-white border-red-600'
      : 'btn btn-primary';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      aria-hidden="true"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-md overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
      >
        <div className="border-b border-gray-100 px-5 py-4">
          <div className="text-base font-semibold text-gray-900">{title}</div>
          {description ? (
            <div className="mt-1 text-sm text-gray-600">{description}</div>
          ) : null}
        </div>

        <div className="space-y-4 px-5 py-4">
          {multiline ? (
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              rows={rows}
              className="min-h-[8rem] w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
            />
          ) : (
            <input
              ref={inputRef}
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
            />
          )}

          {children ? <div>{children}</div> : null}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-4">
          <button type="button" onClick={onCancel} className="btn btn-secondary">
            {cancelText}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={() => onConfirm(value)}
            className={confirmClass}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
